import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import { FedexAccountOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import dayjs from 'dayjs';

interface ComponentProps {
  accounts: FedexAccountOutDTO[];
  onAddAccount: () => void;
  onChanged: () => void;
}

/**
 * FedEx accounts registered by the company. Company locations narrow which of them
 * a shipment may use; with several allowed accounts the distribution form asks for one.
 */
const FedexAccountsSection: React.FC<ComponentProps> = ({ accounts, onAddAccount, onChanged }) => {
  const { tr } = useTranslationLgs();
  const { renameFedexAccount, deleteFedexAccount } = useContext(ApiContext);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteAccount, setDeleteAccount] = useState<FedexAccountOutDTO | null>(null);

  const startEdit = (account: FedexAccountOutDTO) => {
    setEditingId(account.id);
    setEditName(account.name ?? '');
  };

  const saveName = (accountId: string) => {
    renameFedexAccount(accountId, { name: editName.trim() || null }, () => {
      setEditingId(null);
      onChanged();
    });
  };

  const confirmDelete = () => {
    if (!deleteAccount) return;
    deleteFedexAccount(deleteAccount.id, () => {
      setDeleteAccount(null);
      onChanged();
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <COHeadline variant={'h4'} title={tr('CompanyProfileFedex.accounts.title', 'Connected FedEx accounts')} />
      <div className="flex flex-col gap-2">
        {accounts.map(account => (
          <div
            key={account.id}
            className="flex flex-row items-center gap-4 p-3 rounded-lg border border-content-quaternary"
            data-testid={`fedex-account-${account.accountNumber}`}
          >
            <div className="flex flex-col min-w-[140px]">
              <span className="font-medium text-content-primary">{account.accountNumber}</span>
              {account.createdAt && (
                <span className="text-xs text-content-secondary">
                  {tr('CompanyProfileFedex.accounts.addedAt', 'Added {{date}}', { date: dayjs(account.createdAt).format('DD.MM.YYYY') })}
                </span>
              )}
            </div>
            <div className="flex-1">
              {editingId === account.id ? (
                <InputText
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  maxLength={100}
                  placeholder={tr('CompanyProfileFedex.accountName', 'Account name (e.g. the site it ships from)')}
                  style={{ width: '100%' }}
                  autoFocus
                />
              ) : (
                <span className="text-content-secondary">{account.name ?? '—'}</span>
              )}
            </div>
            <div className="flex flex-row gap-2">
              {editingId === account.id ? (
                <>
                  <RingilButton mode={'filled'} label={tr('CompanyProfileFedex.accounts.save', 'Save')} onClick={() => saveName(account.id)} />
                  <RingilButton mode={'text-only'} label={tr('CompanyProfileFedex.accounts.cancel', 'Cancel')} onClick={() => setEditingId(null)} />
                </>
              ) : (
                <>
                  <RingilButton mode={'text-only'} label={tr('CompanyProfileFedex.accounts.rename', 'Rename')} onClick={() => startEdit(account)} />
                  <RingilButton mode={'text-only'} label={tr('CompanyProfileFedex.accounts.remove', 'Remove')} onClick={() => setDeleteAccount(account)} />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <div>
        <RingilButton label={tr('CompanyProfileFedex.addAccount', 'Add another FedEx account')} onClick={onAddAccount} />
      </div>

      <ConfirmDialog
        visible={deleteAccount != null}
        dialogTitle={tr('CompanyProfileFedex.accounts.removeTitle', 'Remove FedEx account')}
        dialogMessage={tr(
          'CompanyProfileFedex.accounts.removeMessage',
          'Remove FedEx account {{accountNumber}}? It is also removed from all locations. Shipments already created on it keep their tracking.',
          { accountNumber: deleteAccount?.accountNumber },
        )}
        onCancel={() => setDeleteAccount(null)}
        onConfirm={confirmDelete}
        onAcceptButtonText={tr('CompanyProfileFedex.accounts.removeConfirm', 'Remove')}
        onCancelButtonText={tr('CompanyProfileFedex.accounts.cancel', 'Cancel')}
      />
    </div>
  );
};

export default FedexAccountsSection;
